import React from "react";
import { useTranslation } from "react-i18next";
import {
    Card,
    SectionHeader,
    SectionTitle,
    SmallToggle,
    ResetButton,
    PreviewModeContainer,
    PreviewModeButton,
} from "./styled";
import {
    PREVIEW_MODE_WITH_ORDER,
    PREVIEW_MODE_NO_ORDER,
} from "../shared/constants";

type OrderOptionsCardProps = {
    mainColor: string;
    showStepper: boolean;
    onToggleStepper: () => void;
    showOrderDetails: boolean;
    onToggleOrderDetails: () => void;
    previewMode: "with_order" | "no_order";
    onPreviewModeChange: (mode: "with_order" | "no_order") => void;
    onResetAll: () => void;
};

export function OrderOptionsCard({
    mainColor,
    showStepper,
    onToggleStepper,
    showOrderDetails,
    onToggleOrderDetails,
    previewMode,
    onPreviewModeChange,
    onResetAll,
}: OrderOptionsCardProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-order-options-card">
            <SectionHeader>
                <SectionTitle>{t("Order section")}</SectionTitle>
                <ResetButton
                    data-testid="scan-to-order-reset-all"
                    mainColor={mainColor}
                    onClick={onResetAll}
                    title={t("Reset all")}
                    style={{
                        padding: "6px 12px",
                        width: "auto",
                        height: "auto",
                    }}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{ width: 14, height: 14 }}
                    >
                        <path d="M1 4v6h6M23 20v-6h-6" />
                        <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                    </svg>
                </ResetButton>
            </SectionHeader>

            {/* Show order status toggle */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 15,
                }}
            >
                <span style={{ fontSize: 13, color: "#333" }}>
                    {t("Show order status")}
                </span>
                <SmallToggle
                    data-testid="scan-to-order-toggle-stepper"
                    isActive={showStepper}
                    mainColor={mainColor}
                    onClick={onToggleStepper}
                    aria-label={t("Show order status")}
                />
            </div>

            {/* Show order details toggle */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 15,
                }}
            >
                <span style={{ fontSize: 13, color: "#333" }}>
                    {t("Show order details")}
                </span>
                <SmallToggle
                    data-testid="scan-to-order-toggle-details"
                    isActive={showOrderDetails}
                    mainColor={mainColor}
                    onClick={onToggleOrderDetails}
                    aria-label={t("Show order details")}
                />
            </div>

            {/* Preview mode */}
            <span
                style={{
                    fontSize: 12,
                    color: "#999",
                    marginBottom: 8,
                    display: "block",
                }}
            >
                {t("Preview mode")}
            </span>
            <PreviewModeContainer data-testid="scan-to-order-preview-mode-container">
                <PreviewModeButton
                    data-testid="scan-to-order-preview-with-order"
                    isActive={previewMode === PREVIEW_MODE_WITH_ORDER}
                    mainColor={mainColor}
                    onClick={() => onPreviewModeChange(PREVIEW_MODE_WITH_ORDER)}
                >
                    <svg viewBox="0 0 24 24">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                    </svg>
                    {t("With order")}
                </PreviewModeButton>
                <PreviewModeButton
                    data-testid="scan-to-order-preview-without-order"
                    isActive={previewMode === PREVIEW_MODE_NO_ORDER}
                    mainColor={mainColor}
                    onClick={() => onPreviewModeChange(PREVIEW_MODE_NO_ORDER)}
                >
                    <svg viewBox="0 0 24 24">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                    {t("Without order")}
                </PreviewModeButton>
            </PreviewModeContainer>
        </Card>
    );
}
